/**
 * Design tokens: the one source for colours, type, radii, shadows and sizes.
 * Spec: docs/superpowers/specs/2026-10-20-frontend-ui-redesign-design.md
 *
 * src/styles/tokens.css mirrors these as CSS variables for Tailwind and CSS
 * modules (tokens.test.js fails when the two drift); src/config/antd-theme.js
 * builds the antd theme from them. This is the only JS file allowed to hold
 * colour literals.
 */

export const color = {
  surface: "#FFFFFF",
  // Gray, so white cards stand off the page (the near-white #FCFCFB read flat).
  canvas: "#F2F2F0",
  sunken: "#F7F6F4",
  hover: "#F0EFEC",
  line: "#E2E1DD",
  "line-strong": "#DDDCD8",
  "ink-faint": "#A3A29D",
  "ink-muted": "#5A5955",
  "ink-strong": "#3B3A37",
  ink: "#1D1D1B",
  accent: "#1F5FD1",
  "accent-hover": "#1A52B8",
  "accent-subtle": "#EAF0FB",
  // The navy icon rail and what sits on it.
  sidebar: "#0E1735",
  "sidebar-ink": "rgba(255, 255, 255, 0.62)",
  "sidebar-ink-faint": "rgba(255, 255, 255, 0.4)",
  "sidebar-ink-active": "#FFFFFF",
  "sidebar-hover": "rgba(255, 255, 255, 0.08)",
  "sidebar-selected": "rgba(255, 255, 255, 0.14)",
  "sidebar-line": "rgba(255, 255, 255, 0.12)",
};

/** The only five status tones. fg = text, bg = pill background, dot = the dot and antd's seed. */
export const status = {
  success: { bg: "#E3F4EA", fg: "#1D7A46", dot: "#34A36B" },
  warning: { bg: "#FDF0DC", fg: "#A65D0B", dot: "#E8962F" },
  danger: { bg: "#FBE7E6", fg: "#B4302A", dot: "#E0554D" },
  info: { bg: "#E8EEFC", fg: "#2F55C9", dot: "#5B7FE3" },
  neutral: { bg: "#F0EFEC", fg: "#6F6E69", dot: "#A3A29D" },
};

/** Type steps: size and line height in px. */
export const type = {
  "page-title": { size: 22, lineHeight: 28, weight: 700, tracking: "-0.015em" },
  "dialog-title": { size: 15, lineHeight: 22, weight: 600 },
  "section-title": { size: 14, lineHeight: 20, weight: 600 },
  body: { size: 13, lineHeight: 20, weight: 400 },
  meta: { size: 12, lineHeight: 16, weight: 500 },
  caption: { size: 11, lineHeight: 14, weight: 550 },
};

/** Radii in px. */
export const radius = { control: 6, card: 10, pill: 999 };

export const focus = { outline: "rgba(31, 95, 209, 0.16)" };

export const shadow = {
  popover: "0 8px 24px rgba(29, 29, 27, 0.12), 0 1px 2px rgba(29, 29, 27, 0.06)",
  dialog: "0 16px 48px rgba(29, 29, 27, 0.18), 0 0 0 1px rgba(29, 29, 27, 0.06)",
  focus: `0 0 0 3px ${focus.outline}`,
};

export const font = {
  sans: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "ui-monospace, 'SF Mono', Menlo, monospace",
};

/** Sizes in px: antd control heights, and the sidebar open / folded. */
export const size = {
  control: 28,
  controlSM: 24,
  controlLG: 32,
  sidebar: 208,
  sidebarRail: 52,
};

/** Every token as the CSS custom property tokens.css must declare: name -> value. */
export function cssVariables() {
  const vars = {};
  for (const [key, value] of Object.entries(color)) vars[`--color-${key}`] = value;
  for (const [tone, { bg, fg, dot }] of Object.entries(status)) {
    vars[`--color-${tone}`] = fg;
    vars[`--color-${tone}-bg`] = bg;
    vars[`--color-${tone}-dot`] = dot;
  }
  for (const [step, t] of Object.entries(type)) {
    vars[`--text-${step}`] = `${t.size}px`;
    vars[`--text-${step}--line-height`] = `${t.lineHeight}px`;
    vars[`--text-${step}--font-weight`] = String(t.weight);
    if (t.tracking) vars[`--text-${step}--letter-spacing`] = t.tracking;
  }
  for (const [key, value] of Object.entries(radius)) vars[`--radius-${key}`] = `${value}px`;
  for (const [key, value] of Object.entries(shadow)) vars[`--shadow-${key}`] = value;
  for (const [key, value] of Object.entries(font)) vars[`--font-${key}`] = value;
  return vars;
}
